import { inject } from '@angular/core';
import { CanActivateChildFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Company } from '@core/models/company';
import { AuthService } from '@core/services/auth.service';
import { CompanyService } from '@core/services/company.service';
import { LoggingService } from '@core/services/logging.service';

/** The plan step: plans and checkout, or the partner note for a partner company. */
export const PLAN_STEP_URL = '/dashboard/company/subscription-plans';

/**
 * Dashboard pages a company may use before it has a plan (#943): the plan step
 * itself, its company and billing pages, the user's own profile, support, and
 * the first-scan page (which waits for Stripe to confirm the plan).
 */
export const ALLOWED_WITHOUT_PLAN: readonly string[] = [
  PLAN_STEP_URL,
  '/dashboard/company',
  '/dashboard/company/edit',
  '/dashboard/my-account',
  '/dashboard/support',
  '/dashboard/first-scan',
];

/** Sections whose sub-pages are all allowed (profile and support pages). */
const ALLOWED_WITHOUT_PLAN_PREFIXES: readonly string[] = [
  '/dashboard/my-account/',
  '/dashboard/support/',
];

/**
 * Stored tiers that mean "no plan". 'free' is the value a new company is
 * created with (it grants no Coins, scans or AI); 'expired' is a lapsed
 * sales trial.
 */
const NO_PLAN_TIERS: ReadonlySet<string> = new Set(['', 'free', 'expired']);

/**
 * True when the company has a plan: a paid tier set by the Stripe webhook, or
 * a tier staff assigned (including a sales trial).
 *
 * @param company The company, or nothing.
 */
export function hasActivePlan(company: Pick<Company, 'tier'> | null | undefined): boolean {
  return !!company && !NO_PLAN_TIERS.has(String(company.tier ?? ''));
}

/**
 * True when the URL may be used without a plan.
 *
 * @param url A router URL, with or without query and fragment.
 */
export function isAllowedWithoutPlan(url: string): boolean {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  return (
    ALLOWED_WITHOUT_PLAN.includes(path) ||
    ALLOWED_WITHOUT_PLAN_PREFIXES.some((prefix) => path.startsWith(prefix))
  );
}

/**
 * planRequiredGuard (#943)
 *
 * Until a company has a plan, its users go to the plan step: a self-serve
 * company to the plans and checkout, a channel-partner company to the note
 * that its partner or Scan Ninja will set up its plan (the same page shows
 * one or the other). Only the pages in {@link ALLOWED_WITHOUT_PLAN} stay open.
 *
 * This is navigation, not security: the API refuses scans, AI and Coins
 * without a plan. So it fails open when the company cannot be read, and the
 * cached company is re-read before redirecting, because the Stripe webhook
 * may have set the tier since it was loaded.
 */
export const planRequiredGuard: CanActivateChildFn = async (
  _route,
  state: RouterStateSnapshot,
): Promise<boolean | UrlTree> => {
  if (isAllowedWithoutPlan(state.url)) {
    return true;
  }

  const authService = inject(AuthService);
  const companyService = inject(CompanyService);
  const router = inject(Router);
  const logging = inject(LoggingService);

  try {
    const user = await authService.waitForHydratedUser(10000);
    const companyId = user?.currentCompanyId;
    if (!companyId) {
      // Registration owns company-less users.
      return true;
    }

    const cached = companyService.company;
    if (cached?.uid === companyId && hasActivePlan(cached)) {
      return true;
    }

    const fresh = await companyService.getCompany(companyId);
    if (!fresh) {
      return true;
    }
    companyService.company = fresh;
    return hasActivePlan(fresh) ? true : router.parseUrl(PLAN_STEP_URL);
  } catch (error) {
    logging.logWarning('planRequiredGuard: could not read the company; allowing navigation', { error });
    return true;
  }
};
